import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import {
    ChevronRight,
    ShoppingCart,
    Truck,
    ShieldCheck,
    AlertCircle,
    CalendarIcon,
    Plus,
    Minus,
    Maximize2,
    ArrowLeft
} from "lucide-react";
import useEmblaCarousel from 'embla-carousel-react';
import { getImageUrl } from "../../utils/imageUtils";
import { useCart } from "../../context/CartContext";
import { cn } from "@/lib/utils";
import { isPlaceholderVariant, uniqueVariants, variantLabel } from "@/lib/variants";
import { funeralStoreService } from "../../services/funeralStore";

export default function FuneralStoreProductDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToCart, setCartOpen } = useCart();

    const [product, setProduct] = useState(null);
    const [variants, setVariants] = useState([]);
    const [loading, setLoading] = useState(true);

    // Selection State
    const [quantity, setQuantity] = useState(1);
    const [customizationValues, setCustomizationValues] = useState({});
    const [validationErrors, setValidationErrors] = useState({});

    // Gallery State
    const [selectedImage, setSelectedImage] = useState("");
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, skipSnaps: false });
    const [selectedIndex, setSelectedIndex] = useState(0);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on('select', onSelect);
    }, [emblaApi, onSelect]);

    const scrollTo = useCallback((index) => {
        if (!emblaApi) return;
        emblaApi.scrollTo(index);
    }, [emblaApi]);

    const productImages = useMemo(() => {
        if (!product) return [];
        const main = product.image_url || "/placeholder2.jpg";
        const additional = product.images?.map(img => img.url) || [];
        const all = [main, ...additional].filter(Boolean);
        return Array.from(new Set(all));
    }, [product]);

    useEffect(() => {
        fetchProductDetails();
    }, [id]);

    const fetchProductDetails = async () => {
        setLoading(true);
        try {
            const data = await funeralStoreService.reseller.getProductById(id);
            let productData = data.product;
            const variantsData = uniqueVariants(data.variants);

            // 1. Variation Patching (Images, Description, Name)
            if (productData.parent_id) {
                const parent = variantsData.find(v => v.product_id === productData.parent_id || (!v.parent_id && v.product_id === productData.parent_id));
                if (parent) {
                    // Fallback description
                    if (!productData.description || productData.description.trim() === '') {
                        productData.description = parent.description;
                    }
                    // Fallback images
                    if (!productData.image_url && (!productData.images || productData.images.length === 0)) {
                        productData.image_url = parent.image_url;
                        productData.images = parent.images;
                    }
                    // Informative Title: "Parent Name - Variant Name" ("Default Title" adds nothing)
                    if (!productData.name || productData.name === parent.name || productData.name === productData.sku) {
                        productData.name = !isPlaceholderVariant(productData.variant_name)
                            ? `${parent.name} - ${productData.variant_name}`
                            : parent.name;
                    }
                }
            }

            setProduct(productData);
            setVariants(variantsData);
            setSelectedImage(productData.image_url || "/placeholder2.jpg");
            
            // Reset selection
            setQuantity(1);
            setCustomizationValues({});
            setValidationErrors({});
        } catch (error) {
            toast.error("Failed to load product details");
            navigate("/reseller/funeral-store");
        } finally {
            setLoading(false);
        }
    };

    const totalPrice = useMemo(() => {
        if (!product) return 0;
        const base = product.overridden_price || product.base_price || 0;
        return base * quantity;
    }, [product, quantity]);

    const handleAddToCart = () => {
        const item = {
            uniqueKey: `${product.product_id}-${Date.now()}`,
            product_id: product.product_id,
            product_type: 'funeral_store',
            name: product.name,
            base_price: product.base_price,
            total_price: product.overridden_price || product.base_price,
            image_url: product.image_url,
            quantity,
            customizations: {}, // Simplified for now
            variant_name: isPlaceholderVariant(product.variant_name) ? undefined : product.variant_name
        };
        addToCart(item);
        toast.success("Added to cart!");
    };

    const activeCollections = useMemo(() => {
        if (!product) return [];
        return (product.collections || []).filter(c => c.is_active !== false);
    }, [product]);

    if (loading) return <div className="p-12 text-center text-slate-500">Loading product...</div>;
    if (!product) return null;

    return (
        <div className="space-y-8 animate-fade-in">
            <button 
                onClick={() => navigate("/reseller/funeral-store")}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-indigo-600 transition-colors"
            >
                <ArrowLeft className="w-4 h-4" />
                Back to Catalog
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                {/* Gallery */}
                <div className="lg:col-span-6">
                    <div className="sticky top-24 space-y-4">
                        <div className="relative group">
                            <div className="overflow-hidden rounded-3xl bg-white aspect-square border-4 border-white shadow-xl" ref={emblaRef}>
                                <div className="flex h-full">
                                    {productImages.map((img, i) => (
                                        <div key={i} className="flex-[0_0_100%] min-w-0 relative h-full flex items-center justify-center p-8">
                                            <img
                                                src={getImageUrl(img)}
                                                alt={`${product.name} - ${i}`}
                                                className="max-h-full max-w-full object-contain mix-blend-multiply transition-transform duration-500 group-hover:scale-105"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {productImages.length > 1 && (
                            <div className="flex gap-3 justify-center">
                                {productImages.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={() => scrollTo(i)}
                                        className={cn(
                                            "w-16 h-16 rounded-xl bg-white border-2 flex-shrink-0 p-2 transition-all",
                                            selectedIndex === i ? "border-indigo-600 shadow-md scale-105" : "border-slate-100 hover:border-slate-300"
                                        )}
                                    >
                                        <img src={getImageUrl(img)} alt="" className="w-full h-full object-contain mix-blend-multiply" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Details */}
                <div className="lg:col-span-6 space-y-8">
                    <div className="space-y-4">
                        <div className="flex flex-wrap gap-x-2 gap-y-2">
                            {activeCollections.length > 0 ? (
                                activeCollections.map((c, i) => (
                                    <Link
                                        key={c.collection_id || i}
                                        to={`/reseller/funeral-store?collection_id=${c.collection_id}`}
                                        className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all hover:scale-105 active:scale-95 shadow-sm border border-indigo-100 bg-indigo-50 text-indigo-600"
                                    >
                                        {c.name}
                                    </Link>
                                ))
                            ) : (
                                <p className="text-amber-700 font-bold uppercase tracking-[0.2em] text-xs">
                                    All Products
                                </p>
                            )}
                        </div>
                        <h1 className="text-3xl font-bold text-slate-900 leading-tight">{product.name}</h1>
                        <div className="flex items-center justify-between border-b pb-4">
                            <p className="text-xs text-slate-400 font-mono">SKU: {product.sku || 'N/A'}</p>
                            <div className="text-right">
                                <span className="text-3xl font-bold text-indigo-600">
                                    ${(product.overridden_price || product.base_price || 0).toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <p className="text-slate-600 leading-relaxed whitespace-pre-line">{product.description}</p>

                    {variants.length > 1 && (
                        <div className="space-y-3">
                            <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Available Variations</Label>
                            <div className="flex flex-col gap-2">
                                {variants.map(v => (
                                    <Button
                                        key={v.product_id}
                                        variant="outline"
                                        onClick={() => navigate(`/reseller/funeral-store/product/${v.product_id}`)}
                                        className={cn(
                                            "h-auto py-3 px-4 rounded-xl flex flex-col items-start gap-1 text-left whitespace-normal",
                                            v.product_id === product.product_id ? "border-indigo-600 bg-indigo-50 text-indigo-900 shadow-sm" : "border-slate-200"
                                        )}
                                    >
                                        <div className="text-sm font-bold w-full">{variantLabel(v, (variants.find(x => !x.parent_id) || product).name)}</div>
                                        <div className="text-xs opacity-60">${(v.overridden_price || v.base_price || 0).toFixed(2)}</div>
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="space-y-4 pt-6">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center border-2 border-slate-100 bg-white rounded-xl p-1">
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-9 w-9" 
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                >
                                    <Minus className="w-4 h-4" />
                                </Button>
                                <Input 
                                    className="w-12 border-none bg-transparent text-center font-bold text-slate-900" 
                                    value={quantity}
                                    readOnly 
                                />
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-9 w-9" 
                                    onClick={() => setQuantity(quantity + 1)}
                                >
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>
                            <Button 
                                onClick={handleAddToCart}
                                className="flex-1 bg-indigo-600 hover:bg-black text-white py-6 text-lg rounded-2xl shadow-lg shadow-indigo-100"
                            >
                                <ShoppingCart className="w-5 h-5 mr-3" />
                                Add to Cart - ${totalPrice.toFixed(2)}
                            </Button>
                        </div>
                    </div>

                    {/* Trust Badges - Hidden as per request */}
                    {/* 
                    <div className="grid grid-cols-2 gap-4 pt-8">
                        <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex items-start gap-3">
                            <Truck className="w-5 h-5 text-indigo-500" />
                            <div>
                                <p className="font-bold text-slate-900 text-sm">Direct Shipping</p>
                                <p className="text-xs text-slate-500">Fast delivery to your home.</p>
                            </div>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex items-start gap-3">
                            <ShieldCheck className="w-5 h-5 text-indigo-500" />
                            <div>
                                <p className="font-bold text-slate-900 text-sm">Quality Guaranteed</p>
                                <p className="text-xs text-slate-500">Premium funeral supplies.</p>
                            </div>
                        </div>
                    </div>
                    */}
                </div>
            </div>
        </div>
    );
}
